/**
 * Backgammon data structures — a verbatim port of Client/Assets/Code/CommonClasses/BackgammonCommon.cs
 * (DicePack, SingleMove, MovePacket, BeadPack, BeadRow, UndoRow, UndoPack) plus the HighLightColumnDto
 * from BackgammonLogic.cs.
 *
 * Behavioural fidelity with the C# is mandatory (see ./constants.ts). Several non-obvious C# semantics
 * are preserved deliberately and called out in comments — do NOT "simplify" them.
 */

import {
  BeadColor,
  BACKGAMMON_WAIT_ROW_WHITE,
  BACKGAMMON_WAIT_ROW_BLACK,
  BACKGAMMON_OUT_ROW_WHITE,
  BACKGAMMON_OUT_ROW_BLACK,
  BACKGAMMON_START_ROW,
  BACKGAMMON_ROWS_COUNT,
} from './constants';

/** Mirrors List<T>.Remove(item): removes the FIRST matching element, returns whether one was found. */
function removeFirst(arr: number[], value: number): boolean {
  const i = arr.indexOf(value);
  if (i < 0) return false;
  arr.splice(i, 1);
  return true;
}

/** A legal-move suggestion: a destination row and the dice (one or several) needed to reach it. */
export interface HighLightColumnDto {
  targetRow: number;
  diceNeed: number[];
}

/**
 * The dice a player has to play this turn. On equal dice the player gets four (handled server-side in
 * throwDice). The client receives Availables/Used over the wire and never rolls on the network path —
 * throwDice() here is a Math.random stand-in for offline/tests only and is intentionally NOT a
 * reproduction of the C# System.Random sequence.
 */
export class DicePack {
  availables: number[] = [];
  used: number[] = [];

  readyToThrowDice(): void {
    this.availables = [];
    this.used = [];
  }

  /** All dice consumed (and at least one was rolled). */
  get allDone(): boolean {
    return this.availables.length === 0 && this.used.length > 0;
  }

  /** No dice left to play — i.e. it's time to roll. */
  get isReadyToThrowDice(): boolean {
    return this.availables.length === 0;
  }

  useDice(dice: number): void {
    if (removeFirst(this.availables, dice)) {
      this.used.push(dice);
    }
  }

  restoreDice(dice: number): void {
    if (removeFirst(this.used, dice)) {
      this.availables.push(dice);
    }
  }

  /** Off the network path — see class doc. rng defaults to Math.random; inject for determinism. */
  throwDice(rng: () => number = Math.random): void {
    this.availables = [];
    const d1 = 1 + Math.floor(rng() * 6);
    let d2 = 1 + Math.floor(rng() * 6);
    if (d1 === d2) {
      const luck = Math.floor(rng() * 10);
      if (luck < 3) {
        this.availables = [d1, d1, d1, d1];
      } else {
        d2 = 1 + Math.floor(rng() * 6);
        this.availables = d1 === d2 ? [d1, d1, d1, d1] : [d1, d2];
      }
    } else {
      this.availables = [d1, d2];
    }
  }
}

/** One atomic hop of a checker (a single die used). */
export class SingleMove {
  constructor(
    public from: number,
    public to: number,
    public dice: number,
  ) {}
}

/**
 * A complete move of one checker — possibly several hops (e.g. using both dice, or chaining doubles).
 * In C# this is `MovePacket : List<SingleMove>`; here it wraps an array and is iterable so
 * `for (const m of packet)` reads the same. add() dedups by (from,to,dice) exactly like the C#.
 */
export class MovePacket {
  readonly moves: SingleMove[] = [];

  /** With args: seed a one-hop packet (the C# `MovePacket(from,to,dice)` ctor). No args: empty. */
  constructor(from?: number, to?: number, dice?: number) {
    if (from !== undefined && to !== undefined && dice !== undefined) {
      this.moves.push(new SingleMove(from, to, dice));
    }
  }

  /** Append a hop unless an identical one already exists (C# MovePacket.Add dedup). */
  add(from: number, to: number, dice: number): void {
    if (!this.moves.some((s) => s.from === from && s.to === to && s.dice === dice)) {
      this.moves.push(new SingleMove(from, to, dice));
    }
  }

  get start(): number {
    return this.moves[0].from;
  }

  get end(): number {
    return this.moves[this.moves.length - 1].to;
  }

  get dices(): number[] {
    return this.moves.map((m) => m.dice);
  }

  /** C# `this.Last()` — used by the recursion to continue a chain from its current head. */
  last(): SingleMove | undefined {
    return this.moves[this.moves.length - 1];
  }

  get length(): number {
    return this.moves.length;
  }

  clone(): MovePacket {
    const copy = new MovePacket();
    for (const m of this.moves) {
      copy.add(m.from, m.to, m.dice);
    }
    return copy;
  }

  [Symbol.iterator](): Iterator<SingleMove> {
    return this.moves[Symbol.iterator]();
  }
}

/**
 * One point/row of the board. CRITICAL quirk: Color returns NotSet whenever Count==0 regardless of the
 * backing colour — hit detection, hasBeadsInGameRows and allBeadsAreOut all depend on this.
 */
export class BeadRow {
  rowIndex = 0;
  count = 0;
  private clr: BeadColor = BeadColor.NotSet;

  constructor(rowIndex = 0) {
    this.rowIndex = rowIndex;
  }

  get color(): BeadColor {
    if (this.count === 0) {
      return BeadColor.NotSet;
    }
    return this.clr;
  }

  set color(value: BeadColor) {
    this.clr = value;
  }

  addBead(count: number, color?: BeadColor): void {
    this.count += count;
    if (color !== undefined && color !== BeadColor.NotSet) {
      this.color = color;
    }
  }

  removeBead(count: number): void {
    if (this.count < count) {
      this.count = 0;
    } else {
      this.count -= count;
    }
    if (this.count === 0) {
      this.color = BeadColor.NotSet;
    }
  }
}

/** The full board: 24 game rows + a wait (bar) row and an out (bear-off) row per colour. */
export class BeadPack {
  gameRows: BeadRow[] = [];
  waitRow: Map<BeadColor, BeadRow> = new Map();
  outRow: Map<BeadColor, BeadRow> = new Map();

  /** Resolve a row index (incl. the four sentinels) to its BeadRow — mirrors BeadPack.GameRow. */
  gameRow(rowIndex: number): BeadRow {
    switch (rowIndex) {
      case BACKGAMMON_WAIT_ROW_WHITE:
        return this.waitRow.get(BeadColor.White)!;
      case BACKGAMMON_WAIT_ROW_BLACK:
        return this.waitRow.get(BeadColor.Black)!;
      case BACKGAMMON_OUT_ROW_WHITE:
        return this.outRow.get(BeadColor.White)!;
      case BACKGAMMON_OUT_ROW_BLACK:
        return this.outRow.get(BeadColor.Black)!;
      default:
        return this.gameRows[rowIndex];
    }
  }

  setupEmptyBoard(): void {
    this.gameRows = [];
    for (let i = BACKGAMMON_START_ROW; i < BACKGAMMON_ROWS_COUNT; i++) {
      this.gameRows.push(new BeadRow(i));
    }
    this.waitRow = new Map([
      [BeadColor.White, new BeadRow(BACKGAMMON_WAIT_ROW_WHITE)],
      [BeadColor.Black, new BeadRow(BACKGAMMON_WAIT_ROW_BLACK)],
    ]);
    this.outRow = new Map([
      [BeadColor.White, new BeadRow(BACKGAMMON_OUT_ROW_WHITE)],
      [BeadColor.Black, new BeadRow(BACKGAMMON_OUT_ROW_BLACK)],
    ]);
  }

  setupStandardGame(): void {
    this.setupEmptyBoard();
    this.gameRows[0].addBead(2, BeadColor.White);
    this.gameRows[5].addBead(5, BeadColor.Black);
    this.gameRows[7].addBead(3, BeadColor.Black);
    this.gameRows[11].addBead(5, BeadColor.White);
    this.gameRows[12].addBead(5, BeadColor.Black);
    this.gameRows[16].addBead(3, BeadColor.White);
    this.gameRows[18].addBead(5, BeadColor.White);
    this.gameRows[23].addBead(2, BeadColor.Black);
  }

  setupMoveBeadsOut(fillAllRows = false): void {
    this.setupEmptyBoard();
    if (fillAllRows) {
      for (let i = BACKGAMMON_START_ROW; i < BACKGAMMON_START_ROW + 6; i++) {
        this.gameRows[i].addBead(2, BeadColor.Black);
      }
      for (let i = BACKGAMMON_ROWS_COUNT - 6; i < BACKGAMMON_ROWS_COUNT; i++) {
        this.gameRows[i].addBead(3, BeadColor.White);
      }
    } else {
      this.outRow.get(BeadColor.White)!.count = 2;
      this.outRow.get(BeadColor.Black)!.count = 3;
      this.gameRows[0].addBead(4, BeadColor.Black);
      this.gameRows[1].addBead(4, BeadColor.Black);
      this.gameRows[2].addBead(6, BeadColor.Black);
      this.gameRows[23].addBead(4, BeadColor.White);
      this.gameRows[22].addBead(4, BeadColor.White);
      this.gameRows[21].addBead(4, BeadColor.White);
    }
  }

  setupMoveBeadsOutWhileOppHasBead(): void {
    this.setupEmptyBoard();
    this.outRow.get(BeadColor.White)!.count = 1;
    this.outRow.get(BeadColor.Black)!.count = 0;
    this.waitRow.get(BeadColor.Black)!.count = 1;
    this.gameRows[3].addBead(2, BeadColor.Black);
    this.gameRows[5].addBead(3, BeadColor.Black);
    this.gameRows[7].addBead(3, BeadColor.Black);
    this.gameRows[12].addBead(5, BeadColor.Black);
    this.gameRows[18].addBead(1, BeadColor.Black);
    for (let i = 19; i < 24; i++) {
      this.gameRows[i].addBead(2, BeadColor.White);
    }
  }

  setupHitBeads(): void {
    this.setupEmptyBoard();
    this.gameRows[0].addBead(1, BeadColor.White);
    this.gameRows[5].addBead(1, BeadColor.Black);
    this.gameRows[7].addBead(1, BeadColor.Black);
    this.gameRows[11].addBead(1, BeadColor.White);
    this.gameRows[12].addBead(1, BeadColor.Black);
    this.gameRows[16].addBead(1, BeadColor.White);
    this.gameRows[18].addBead(1, BeadColor.White);
    this.gameRows[23].addBead(1, BeadColor.Black);
  }

  setupNoAllowedMove(): void {
    this.setupEmptyBoard();
    for (let i = BACKGAMMON_START_ROW; i < BACKGAMMON_START_ROW + 6; i++) {
      this.gameRows[i].addBead(3, BeadColor.Black);
    }
    for (let i = BACKGAMMON_ROWS_COUNT - 6; i < BACKGAMMON_ROWS_COUNT; i++) {
      this.gameRows[i].addBead(3, BeadColor.White);
    }
    this.outRow.get(BeadColor.White)!.count = 2;
    this.outRow.get(BeadColor.Black)!.count = 3;
    this.waitRow.get(BeadColor.Black)!.count = 1;
  }
}

/** One undone hop, recording whether it hit an opponent blot (so undo can restore it). */
export class UndoRow {
  constructor(
    public from: number,
    public to: number,
    public dice: number,
    public hit = false,
  ) {}
}

/** The undo history for the current turn (cleared whenever TurnColor changes). */
export class UndoPack {
  undoList: UndoRow[] = [];

  /**
   * Hit-and-run freeze check (verbatim recursion). Returns the frozen row, or -1. cycle caps depth at
   * 4. Finds the FIRST undo whose To == row; if that move hit, return its To; else recurse from its
   * From.
   */
  checkFreezeBead(row: number, cycle = 0): number {
    if (cycle > 4) {
      return -1;
    }
    if (!this.undoList.some((item) => item.hit)) {
      return -1;
    }
    const fromItem = this.undoList.find((item) => item.to === row);
    if (fromItem == null) {
      return -1;
    } else if (fromItem.hit) {
      return fromItem.to;
    } else {
      return this.checkFreezeBead(fromItem.from, cycle + 1);
    }
  }

  clear(): void {
    this.undoList = [];
  }

  insert(from: number, to: number, dice: number, hit = false): void {
    this.undoList.push(new UndoRow(from, to, dice, hit));
  }

  count(): number {
    return this.undoList.length;
  }

  /** Pop the most recent undo entry (or null). */
  fetch(): UndoRow | null {
    if (this.undoList.length > 0) {
      return this.undoList.pop()!;
    }
    return null;
  }
}
